import { Divider, MenuItem } from '@chronosphereio/chrono-ui';
import { cloneElement, isValidElement, ReactNode, useMemo } from 'react';
import { useVizPanelMenuItems } from '@/plugin-system/components/VizPanelContextProvider';
import { VizContainerRootBaseProps } from '../../root/model';
import { UseVizContainerPanelInspectorResult } from './useVizContainerPanelInspector';

export interface UseVizContainerMenuItemsProps extends Pick<VizContainerRootBaseProps, 'menuItems'> {
  panelInspector: UseVizContainerPanelInspectorResult;
}

/**
 * Hook that manages the menu item processing for `VizContainer.Root`.
 */
export const useVizContainerMenuItems = ({
  menuItems: initMenuItems,
  panelInspector,
}: UseVizContainerMenuItemsProps) => {
  const pluginMenuItems = useVizPanelMenuItems();

  const menuItems = useMemo(() => {
    // Switching to an array avoids MUI throwing a bazillion warnings about fragments.
    const updatedMenuItems: ReactNode[] = [];

    if (initMenuItems) {
      if (isValidElement(initMenuItems) && !initMenuItems.key) {
        // Add a key if one is not set to avoid React yelling about a child in an array without a key.
        updatedMenuItems.push(cloneElement(initMenuItems, { key: 'init-menu-items' }));
      } else {
        // Push as is because either the child is not an element (and those we cannot fix keying) or it already has
        // a key.
        updatedMenuItems.push(initMenuItems);
      }
    }

    pluginMenuItems.forEach((item) => {
      updatedMenuItems.push(
        <MenuItem key={item.key} onClick={item.onClick}>
          {item.label}
        </MenuItem>
      );
    });

    if (panelInspector.menuItem) {
      if (updatedMenuItems.length > 0) {
        // Add a divider before the panel inspector item if there are items above it.
        updatedMenuItems.push(<Divider key="inspector-divider" />);
      }
      updatedMenuItems.push(panelInspector.menuItem);
    }

    if (!updatedMenuItems.length) {
      // Empty array, return null so the menu isn't shown.
      return null;
    }

    return updatedMenuItems;
  }, [initMenuItems, panelInspector.menuItem, pluginMenuItems]);

  return menuItems;
};
